import { useState, useCallback, useEffect, useRef } from 'react';
import { FiUploadCloud, FiCheckCircle, FiXCircle, FiAlertCircle, FiFile, FiDatabase, FiDownload, FiFileText, FiX, FiRefreshCw } from 'react-icons/fi';
import { LinearProgress, Alert, AlertTitle, Collapse, TextField, Accordion, AccordionSummary, AccordionDetails, CircularProgress, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import { Box } from '@mui/system';
import { Button } from '../../../components/StyleComponents/StyleComponents';
import trafficAccidentUploadService from '../../../services/TrafficAccidentUploadService';
import partitionService from '../../../services/PartitionService';
import automatedImportService, { ImportHistoryRecord } from '../../../services/AutomatedImportService';
import * as signalR from '@microsoft/signalr';
import { useTranslation } from 'react-i18next';
import './AdministrationTrafficAccidents.css';

interface UploadError {
  message: string;
  fileName?: string;
  rowNumber?: number;
}

interface FileResult {
  fileName: string;
  processed: number;
  errors: number;
}

interface FailedInsertRecord {
  errorMessage: string;
  sqlStatement: string;
}

interface UploadResult {
  totalFiles: number;
  processedFiles: number;
  totalRecords: number;
  insertedRecords: number;
  skippedRecords: number;
  errorRecords: number;
  totalPedestrians: number;
  insertedPedestrians: number;
  skippedPedestrians: number;
  errorPedestrians: number;
  errors: UploadError[];
  fileResults: Record<string, FileResult>;
  failedInsertsSql?: string[];
  failedAccidentInserts?: FailedInsertRecord[];
  failedPedestrianInserts?: FailedInsertRecord[];
  success: boolean;
}

interface FileState {
  file?: File;
  status: 'empty' | 'selected' | 'valid' | 'invalid';
  error?: string;
}

interface PartitionError {
  partitionName: string;
  message: string;
  oracleErrorCode?: string;
}

interface PartitionCreationResult {
  year: number;
  totalRegions: number;
  createdPartitions: number;
  skippedPartitions: number;
  failedPartitions: number;
  createdPartitionNames: string[];
  skippedPartitionNames: string[];
  errors: PartitionError[];
  success: boolean;
  errorMessage?: string;
}

export default function AdministrationTrafficAccidents() {
  const { t } = useTranslation("traffic");
  
  const [vehiclesFile, setVehiclesFile] = useState<FileState>({ status: 'empty' });
  const [accidentsFile, setAccidentsFile] = useState<FileState>({ status: 'empty' });
  const [gpsFile, setGpsFile] = useState<FileState>({ status: 'empty' });
  const [pedestriansFile, setPedestriansFile] = useState<FileState>({ status: 'empty' });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<'uploading' | 'processing'>('uploading');
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  
  // Nové stavy pre zobrazenie importu
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [currentImported, setCurrentImported] = useState(0);
  const [currentSkipped, setCurrentSkipped] = useState(0);
  const [currentErrors, setCurrentErrors] = useState(0);
  const [totalToImport, setTotalToImport] = useState(0);
  const [importCompleted, setImportCompleted] = useState(false);

  // Folder upload mode
  const [folderPath, setFolderPath] = useState<string>('');

  // Partition creation state
  const [partitionYear, setPartitionYear] = useState<number>(new Date().getFullYear());
  const [creatingPartitions, setCreatingPartitions] = useState(false);
  const [partitionResult, setPartitionResult] = useState<PartitionCreationResult | null>(null);
  const [showPartitionErrors, setShowPartitionErrors] = useState(false);
  
  // Partition SQL generation state
  const [generatingSql, setGeneratingSql] = useState(false);
  const [sqlResult, setSqlResult] = useState<any>(null);
  const [partitionExpanded, setPartitionExpanded] = useState(true);

  // Import mode toggle 
  const [importMode, setImportMode] = useState<'manual' | 'automated'>('manual');

  // Automated import state
  const [latestImport, setLatestImport] = useState<ImportHistoryRecord | null>(null);
  const [importHistory, setImportHistory] = useState<ImportHistoryRecord[]>([]);
  const [automatedLoading, setAutomatedLoading] = useState(false);
  const [automatedError, setAutomatedError] = useState<string | null>(null);
  const [triggerYear, setTriggerYear] = useState<number>(new Date().getFullYear() - 1);
  const [triggerEmail, setTriggerEmail] = useState<string>('');
  const [triggering, setTriggering] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [autoEmail, setAutoEmail] = useState<string>('');
  const [autoEmailSaving, setAutoEmailSaving] = useState(false);
  const [autoEmailSaved, setAutoEmailSaved] = useState(false);
  const [scheduleMonthly, setScheduleMonthly] = useState<boolean>(false);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);
  const [triggerMessageIsKey, setTriggerMessageIsKey] = useState(false);
  const [triggerMessageType, setTriggerMessageType] = useState<'success' | 'error'>('success');
  const [downloadingExcel, setDownloadingExcel] = useState<number | null>(null);

  // SignalR connection
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const signalrProgressRef = useRef({ imported: 0, skipped: 0, errors: 0, total: 0 });
  
  // Upload cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Setup SignalR connection
  useEffect(() => {
    // SignalR hub je na /hubs/uploadProgress (BEZ /api)
    const apiBaseUrl = process.env.REACT_APP_API_URL_DN?.replace('/api', '') || 'https://localhost:7111';
    const hubUrl = `${apiBaseUrl}/hubs/uploadProgress`;
    console.log('SignalR Hub URL:', hubUrl);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: false, // Zmena na false pre localhost
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
      })
      .configureLogging(signalR.LogLevel.Debug) 
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    connection.on('ReceiveProgressUpdate', (progress: any) => {
      console.log('SignalR Progress update received:', progress);
      
      setFilesLoaded(progress.filesLoaded);
      setTotalToImport(progress.totalRecords || 0);
      setCurrentImported(progress.insertedRecords || 0);
      setCurrentSkipped(progress.skippedRecords || 0);
      setCurrentErrors(progress.errorRecords || 0);
      setImportCompleted(progress.importCompleted || false);
      signalrProgressRef.current = {
        imported: progress.insertedRecords || 0,
        skipped: progress.skippedRecords || 0,
        errors: progress.errorRecords || 0,
        total: progress.totalRecords || 0,
      };
      
      if (progress.stage === 'processing') {
        setUploadStage('processing');
      }
    });

    connection.onclose((error) => {
      console.log('SignalR connection closed', error);
    });

    connection.onreconnecting((error) => {
      console.log('SignalR reconnecting', error);
    });

    connection.onreconnected((connectionId) => {
      console.log('SignalR reconnected', connectionId);
    });

    connection.start()
      .then(() => {
        console.log('SignalR Connected successfully');
      })
      .catch(err => {
        console.error('SignalR Connection Error:', err);
      });

    return () => {
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.stop();
      }
    };
  }, []);

  const validateExcelFile = (file: File): { valid: boolean; error?: string } => {
    const validExtensions = ['.xls', '.xlsx'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return { valid: false, error: t("AddRecords.upload.invalidFileType") as string };
    }

    if (file.size > 100 * 1024 * 1024) { // 100 MB
      return { valid: false, error: t("AddRecords.upload.fileTooLarge") as string };
    }

    return { valid: true };
  };

  const handleFolderSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    // Reset všetky súbory
    setVehiclesFile({ status: 'empty' });
    setAccidentsFile({ status: 'empty' });
    setGpsFile({ status: 'empty' });
    setPedestriansFile({ status: 'empty' });
    setFolderPath('');

    // Získaj cestu k priečinku z prvého súboru
    const firstFile = files[0];
    const folderName = firstFile.webkitRelativePath.split('/')[0];
    setFolderPath(folderName);

    let foundVehicles = false;
    let foundAccidents = false;
    let foundGps = false;
    let foundPedestrians = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = file.name.toLowerCase();

      // IVozidla.xlsx alebo IVozidla.xls
      if ((fileName === 'ivozidla.xlsx' || fileName === 'ivozidla.xls') && !foundVehicles) {
        const validation = validateExcelFile(file);
        if (validation.valid) {
          setVehiclesFile({ file, status: 'valid' });
          foundVehicles = true;
        } else {
          setVehiclesFile({ file, status: 'invalid', error: validation.error });
        }
      }
      // Inehody.xlsx alebo Inehody.xls
      else if ((fileName === 'inehody.xlsx' || fileName === 'inehody.xls') && !foundAccidents) {
        const validation = validateExcelFile(file);
        if (validation.valid) {
          setAccidentsFile({ file, status: 'valid' });
          foundAccidents = true;
        } else {
          setAccidentsFile({ file, status: 'invalid', error: validation.error });
        }
      }
      // IntGPS.xlsx alebo IntGPS.xls
      else if ((fileName === 'intgps.xlsx' || fileName === 'intgps.xls') && !foundGps) {
        const validation = validateExcelFile(file);
        if (validation.valid) {
          setGpsFile({ file, status: 'valid' });
          foundGps = true;
        } else {
          setGpsFile({ file, status: 'invalid', error: validation.error });
        }
      }
      // Ichodci.xlsx alebo Ichodci.xls (voliteľný)
      else if ((fileName === 'ichodci.xlsx' || fileName === 'ichodci.xls') && !foundPedestrians) {
        const validation = validateExcelFile(file);
        if (validation.valid) {
          setPedestriansFile({ file, status: 'valid' });
          foundPedestrians = true;
        } else {
          setPedestriansFile({ file, status: 'invalid', error: validation.error });
        }
      }
    }

    if (!foundVehicles) {
      setVehiclesFile({ status: 'invalid', error: `${t("AddRecords.upload.vehiclesFile")} ${t("AddRecords.upload.notFound")}` });
    }
    if (!foundAccidents) {
      setAccidentsFile({ status: 'invalid', error: `${t("AddRecords.upload.accidentsFile")} ${t("AddRecords.upload.notFound")}` });
    }
    if (!foundGps) {
      setGpsFile({ status: 'invalid', error: `${t("AddRecords.upload.gpsFile")} ${t("AddRecords.upload.notFound")}` });
    }
  }, [t]);

  const handleUpload = async () => {
    if (!vehiclesFile.file || !accidentsFile.file || !gpsFile.file) {
      alert(t('AddRecords.upload.uploadRequiredFiles'));
      return;
    }

    // Vygenerovanie session ID pre sledovanie
    const uploadSessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setSessionId(uploadSessionId);
    console.log('Generated sessionId:', uploadSessionId);

    const connection = connectionRef.current;
    if (!connection) {
      console.error('✗ SignalR connection ref is null');
      alert(t('AddRecords.upload.signalrNotInitialized'));
      return;
    }

    try {
      if (connection.state !== signalR.HubConnectionState.Connected) {
        console.log('SignalR not connected, attempting to connect...');
        await connection.start();
        console.log('✓ SignalR connection established');
      }

      await connection.invoke('JoinUploadSession', uploadSessionId);
      console.log(`✓ Joined SignalR session: ${uploadSessionId}`);
    } catch (err) {
      console.error('✗ Failed to prepare SignalR:', err);
      alert(t('AddRecords.upload.signalrConnectionError'));
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStage('uploading');
    setUploadResult(null);
    setFilesLoaded(false);
    setCurrentImported(0);
    setCurrentSkipped(0);
    setCurrentErrors(0);
    signalrProgressRef.current = { imported: 0, skipped: 0, errors: 0, total: 0 };
    setTotalToImport(0);
    setImportCompleted(false);

    console.log('Starting upload with sessionId:', uploadSessionId);

    try {
      // Vytvorenie AbortController pre možnosť zrušenia requestu
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const formData = new FormData();
      formData.append('vehiclesFile', vehiclesFile.file);
      formData.append('accidentsFile', accidentsFile.file);
      formData.append('gpsFile', gpsFile.file);
      if (pedestriansFile.file) {
        formData.append('pedestriansFile', pedestriansFile.file);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL_DN}/TrafficAccidentUpload/upload?sessionId=${uploadSessionId}`,
        {
          method: 'POST',
          body: formData,
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        // Špeciálne spracovanie pre status 499 (Client Closed Request)
        if (response.status === 499) {
          throw new Error('CANCELLED');
        }
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const result = await response.json();
      setUploadResult(result);
      setImportCompleted(true);
      setUploadProgress(100);
      setUploadStage('uploading');
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Rozlíšenie či ide o manuálne zrušenie alebo chybu
      if (error.name === 'AbortError' || error.message === 'CANCELLED') {
        console.log('Upload bol zrušený užívateľom');
        const prog = signalrProgressRef.current;
        setUploadResult({
          totalFiles: 0,
          processedFiles: 0,
          totalRecords: prog.total,
          insertedRecords: prog.imported,
          skippedRecords: prog.skipped,
          errorRecords: prog.errors,
          totalPedestrians: 0,
          insertedPedestrians: 0,
          skippedPedestrians: 0,
          errorPedestrians: 0,
          errors: [{ message: t("AddRecords.results.cancelled") }],
          fileResults: {},
          success: false
        });
      } else {
        setUploadResult({
          totalFiles: 0,
          processedFiles: 0,
          totalRecords: 0,
          insertedRecords: 0,
          skippedRecords: 0,
          errorRecords: 0,
          totalPedestrians: 0,
          insertedPedestrians: 0,
          skippedPedestrians: 0,
          errorPedestrians: 0,
          errors: [{ message: error.message || t("AddRecords.results.unexpectedError") }],
          fileResults: {},
          success: false
        });
      }
      setImportCompleted(true);
    } finally {
      abortControllerRef.current = null;
      setUploading(false);
      
      // Odpojenie od SignalR skupiny
      if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
        try {
          await connectionRef.current.invoke('LeaveUploadSession', uploadSessionId);
        } catch (err) {
          console.error('Failed to leave SignalR session:', err);
        }
      }
    }
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      console.log('Cancelling upload...');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleCreatePartitions = async () => {
    if (!partitionYear || partitionYear < 2000 || partitionYear > 2100) {
      alert(t("AddRecords.partitions.invalidYear"));
      return;
    }

    setCreatingPartitions(true);
    setPartitionResult(null);

    try {
      const result = await partitionService.createPartitions(partitionYear);
      setPartitionResult(result);
    } catch (error: any) {
      console.error('Partition creation error:', error);
      setPartitionResult({
        year: partitionYear,
        totalRegions: 0,
        createdPartitions: 0,
        skippedPartitions: 0,
        failedPartitions: 0,
        createdPartitionNames: [],
        skippedPartitionNames: [],
        errors: [],
        success: false,
        errorMessage: error.message || 'Nastala neočakávaná chyba pri vytváraní partícií'
      });
    } finally {
      setCreatingPartitions(false);
    }
  };

  const resetForm = () => {
    setVehiclesFile({ status: 'empty' });
    setAccidentsFile({ status: 'empty' });
    setGpsFile({ status: 'empty' });
    setPedestriansFile({ status: 'empty' });
    setUploadResult(null);
    setUploadProgress(0);
    setShowErrors(false);
    setFolderPath('');
  };

  const resetPartitionForm = () => {
    setPartitionResult(null);
    setSqlResult(null);
    setShowPartitionErrors(false);
    setPartitionYear(new Date().getFullYear());
  };

  const handleGenerateSQL = async () => {
    if (!partitionYear || partitionYear < 2000 || partitionYear > 2100) {
      alert(t("AddRecords.partitions.invalidYear"));
      return;
    }

    setGeneratingSql(true);
    setSqlResult(null);

    try {
      const result = await partitionService.generatePartitionSQL(partitionYear);
      setSqlResult(result);
    } catch (error: any) {
      console.error('SQL generation error:', error);
      setSqlResult({
        year: partitionYear,
        success: false,
        errorMessage: error.message || 'Nepodarilo sa vygenerovať SQL skript'
      });
    } finally {
      setGeneratingSql(false);
    }
  };

  const handleDownloadSQL = async () => {
    if (!partitionYear) {
      alert(t("AddRecords.partitions.invalidYearSQL"));
      return;
    }

    try {
      await partitionService.downloadPartitionSQL(partitionYear);
    } catch (error: any) {
      alert(error.message || t("AddRecords.partitions.downloadError"));
      console.error('Download error:', error);
    }
  };

  const copyToClipboard = () => {
    if (sqlResult?.sqlScript) {
      navigator.clipboard.writeText(sqlResult.sqlScript);
      alert(t("AddRecords.partitions.sqlCopied"));
    }
  };

  const handleDownloadFailedInserts = async () => {
    const accidentInserts = uploadResult?.failedAccidentInserts ?? [];
    const pedestrianInserts = uploadResult?.failedPedestrianInserts ?? [];
    if (accidentInserts.length === 0 && pedestrianInserts.length === 0) return;

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL_DN}/TrafficAccidentUpload/export-errors-excel`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accidentInserts, pedestrianInserts }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `failed_inserts_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export error:', err);
      alert(t('AddRecords.upload.downloadExcelError'));
    }
  };

  const loadAutomatedData = async () => {
    setAutomatedLoading(true);
    setAutomatedError(null);
    try {
      const [latest, history, email, monthly] = await Promise.all([
        automatedImportService.getLatestImport(),
        automatedImportService.getImportHistory(20),
        automatedImportService.getNotificationEmail(),
        automatedImportService.getScheduleMonthly(),
      ]);
      setLatestImport(latest);
      setImportHistory(history);
      setAutoEmail(email);
      setScheduleMonthly(monthly);
    } catch (err: any) {
      setAutomatedError(err.message || t('AddRecords.automatedImport.errorLoadLatestImport'));
    } finally {
      setAutomatedLoading(false);
    }
  };

  const handleSaveAutoEmail = async () => {
    setAutoEmailSaving(true);
    setAutoEmailSaved(false);
    try {
      await automatedImportService.saveNotificationEmail(autoEmail);
      setAutoEmailSaved(true);
      setTimeout(() => setAutoEmailSaved(false), 3000);
    } catch (err: any) {
      alert(err.message || t('AddRecords.automatedImport.errorSaveEmail'));
    } finally {
      setAutoEmailSaving(false);
    }
  };

  useEffect(() => {
    if (importMode === 'automated') {
      loadAutomatedData();
    }
  }, [importMode]);

  const handleTriggerImport = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      await automatedImportService.triggerManualImport(triggerYear, triggerEmail || undefined);
      setTriggerMessageType('success');
      setTriggerMessageIsKey(true);
      setTriggerMessage('AddRecords.automatedImport.triggerSuccess');
      
      setTimeout(loadAutomatedData, 1500);
    } catch (err: any) {
      setTriggerMessageType('error');
      setTriggerMessageIsKey(false);
      setTriggerMessage(err.message || t('AddRecords.automatedImport.triggerError'));
    } finally {
      setTriggering(false);
    }
  };

  const handleCancelImport = async () => {
    setCancelling(true);
    try {
      await automatedImportService.cancelImport();
    } catch {
    } finally {
      setCancelling(false);
      setTimeout(loadAutomatedData, 1000);
    }
  };

  const handleDownloadErrorExcel = async (importId: number, fileName?: string | null) => {
    setDownloadingExcel(importId);
    try {
      await automatedImportService.downloadErrorExcel(importId, fileName ?? undefined);
    } catch (err: any) {
      alert(err.message || t('AddRecords.automatedImport.errorDownloadExcel'));
    } finally {
      setDownloadingExcel(null);
    }
  };

  const getStatusChip = (stav: string) => {
    const statusMap: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }> = {
      SUCCESS: { label: t('AddRecords.automatedImport.statusSuccess'), color: 'success' },
      PARTIAL_SUCCESS: { label: t('AddRecords.automatedImport.statusPartial'), color: 'warning' },
      FAILED: { label: t('AddRecords.automatedImport.statusFailed'), color: 'error' },
      RUNNING: { label: t('AddRecords.automatedImport.statusRunning'), color: 'info' },
    };
    const s = statusMap[stav] ?? { label: stav, color: 'default' };
    return <Chip label={s.label} color={s.color} size="small" />;
  };

  const getFileIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return <FiCheckCircle className="file-icon success" />;
      case 'invalid':
        return <FiXCircle className="file-icon error" />;
      default:
        return <FiFile className="file-icon" />;
    }
  };

  const canUpload = 
    vehiclesFile.status === 'valid' && 
    accidentsFile.status === 'valid' && 
    gpsFile.status === 'valid' &&
    !uploading;

  return (
    <div className="traffic-accidents-upload">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <ToggleButtonGroup>
          <Button
            className={importMode === 'manual' ? '' : 'main-button-peds'}
            onClick={() => setImportMode('manual')}
            style={{ margin: '0px' }}
          >
            {t("AddRecords.automatedImport.toggleManual")}
          </Button>
          <Button
            className={importMode === 'automated' ? '' : 'main-button-peds'}
            onClick={() => setImportMode('automated')}
            style={{ margin: '0px' }}
          >
            {t("AddRecords.automatedImport.toggleAutomated")}
          </Button>
        </ToggleButtonGroup>
      </div>
      {importMode === 'manual' && (
        <>
      {/* Partition Creation Section */}
      <Accordion 
          expanded={partitionExpanded} 
          onChange={() => setPartitionExpanded(!partitionExpanded)}
          sx={{ mt: 0, mb: 0 }}
        >
          <AccordionSummary
            sx={{ 
              backgroundColor: '#fff3cd',
              '&:hover': { backgroundColor: '#ffe69c' }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
              <FiDatabase style={{ fontSize: '24px', color: '#856404' }} />
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#856404' }}>{t("AddRecords.partitions.title")}</h3>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#856404' }}>
                  {t("AddRecords.partitions.description")}
                </p>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className="partition-section">
              {/* Rok input - spoločný pre obe možnosti */}
              <div style={{ marginTop: '20px', marginBottom: '40px' }}>
                <TextField
                  type="number"
                  label={t("AddRecords.partitions.yearLabel")}
                  value={partitionYear}
                  onChange={(e) => setPartitionYear(parseInt(e.target.value) || new Date().getFullYear())}
                  disabled={creatingPartitions || generatingSql}
                  inputProps={{ min: 2000, max: 2100 }}
                  fullWidth
                />
              </div>

              {/* Možnosť 1: Vytvoriť priamo v databáze */}
              <div style={{ 
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiDatabase /> {t("AddRecords.partitions.createDirectly")}
                </h4>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                  {t("AddRecords.partitions.createDirectlyDesc")}
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <Button
                    onClick={creatingPartitions ? undefined : handleCreatePartitions}
                    className={creatingPartitions ? 'disabled' : ''}
                  >
                    {creatingPartitions ? t("AddRecords.partitions.creating") : t("AddRecords.partitions.createButton")}
                  </Button>
                  {partitionResult && (
                    <Button
                      onClick={resetPartitionForm}
                      className="main-button-peds"
                    >
                      {t("AddRecords.upload.resetButton")}
                    </Button>
                  )}
                </div>

                {creatingPartitions && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress variant="indeterminate" />
                    <p className="progress-text">{t("AddRecords.partitions.creating")}</p>
                  </Box>
                )}

                {partitionResult && (
                  <div style={{ marginTop: '20px' }}>
                    <Alert severity={partitionResult.success ? 'success' : 'error'}>
                      <AlertTitle>
                        {partitionResult.success ? t("AddRecords.partitions.successTitle") : t("AddRecords.partitions.errorTitle")}
                      </AlertTitle>
                      <div className="results-summary">
                        <div className="result-item">
                          <strong>{t("AddRecords.partitions.year")}:</strong> {partitionResult.year}
                        </div>
                        <div className="result-item">
                          <strong>{t("AddRecords.partitions.totalRegions")}:</strong> {partitionResult.totalRegions}
                        </div>
                        <div className="result-item success">
                          <FiCheckCircle /> <strong>{t("AddRecords.partitions.createdPartitions")}:</strong> {partitionResult.createdPartitions}
                        </div>
                        {partitionResult.skippedPartitions > 0 && (
                          <div className="result-item warning">
                            <FiAlertCircle /> <strong>{t("AddRecords.partitions.skippedPartitions")}:</strong> {partitionResult.skippedPartitions}
                          </div>
                        )}
                        {partitionResult.failedPartitions > 0 && (
                          <div className="result-item error">
                            <FiXCircle /> <strong>{t("AddRecords.partitions.failedPartitions")}:</strong> {partitionResult.failedPartitions}
                          </div>
                        )}
                      </div>

                      {partitionResult.createdPartitionNames.length > 0 && (
                        <div className="partition-names" style={{ marginTop: '10px' }}>
                          <strong>{t("AddRecords.partitions.createdList")}</strong>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                            {partitionResult.createdPartitionNames.join(', ')}
                          </div>
                        </div>
                      )}

                      {partitionResult.errors.length > 0 && (
                        <>
                          <Button
                            onClick={() => setShowPartitionErrors(!showPartitionErrors)}
                            className="main-button-peds"
                            style={{ marginTop: '10px' }}
                          >
                            {showPartitionErrors ? t("AddRecords.partitions.hideErrors") : `${t("AddRecords.partitions.showErrors")} (${partitionResult.errors.length})`}
                          </Button>
                          <Collapse in={showPartitionErrors}>
                            <div className="error-list">
                              {partitionResult.errors.map((error, index) => (
                                <div key={index} className="error-item">
                                  <FiXCircle className="error-icon" />
                                  <div className="error-details">
                                    <strong>Partícia: {error.partitionName}</strong>
                                    {error.oracleErrorCode && <span> ({t("AddRecords.partitions.oracleError")} {error.oracleErrorCode})</span>}
                                    <p>{error.message}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Collapse>
                        </>
                      )}

                      {partitionResult.errorMessage && (
                        <div style={{ marginTop: '10px', color: '#d32f2f' }}>
                          <strong>{t("AddRecords.partitions.error")}</strong> {partitionResult.errorMessage}
                        </div>
                      )}
                    </Alert>
                  </div>
                )}
              </div>

              {/* Možnosť 2: Generovať SQL skript */}
              <div style={{ 
                padding: '20px',
                backgroundColor: '#e8f4f8',
                borderRadius: '8px'
              }}>
                <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiFileText /> {t("AddRecords.partitions.generateSQL")}
                </h4>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                  {t("AddRecords.partitions.generateSQLDesc")}
                </p>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <Button
                    onClick={generatingSql ? undefined : handleGenerateSQL}
                    className={generatingSql ? 'disabled' : ''}
                  >
                    {generatingSql ? (
                      <>
                        <CircularProgress size={20} style={{ marginRight: '10px' }} />
                        {t("AddRecords.partitions.generating")}
                      </>
                    ) : (
                      <>
                        <FiFileText style={{ marginRight: '8px' }} />
                        {t("AddRecords.partitions.generateButton")}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={(generatingSql || !partitionYear) ? undefined : handleDownloadSQL}
                    className={(generatingSql || !partitionYear) ? 'main-button-peds disabled' : 'main-button-peds'}
                  >
                    <FiDownload style={{ marginRight: '8px' }} />
                    {t("AddRecords.partitions.downloadButton")}
                  </Button>
                </div>

                {sqlResult && sqlResult.success && (
                  <div style={{ marginTop: '20px' }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <AlertTitle>{t("AddRecords.partitions.successGenerated")}</AlertTitle>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FiCheckCircle style={{ fontSize: '20px' }} />
                        <div>
                          <p style={{ margin: '5px 0' }}><strong>{t("AddRecords.partitions.year")}:</strong> {sqlResult.year}</p>
                          <p style={{ margin: '5px 0' }}><strong>{t("AddRecords.partitions.partitionCount")}:</strong> {sqlResult.totalPartitions}</p>
                          {sqlResult.partitionNames && (
                            <div style={{ marginTop: '10px' }}>
                              <strong>{t("AddRecords.partitions.generatedPartitions")}</strong>
                              <ul style={{ margin: '5px 0', paddingLeft: '20px', fontSize: '12px' }}>
                                {sqlResult.partitionNames.map((name: string, index: number) => (
                                  <li key={index}>{name}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </Alert>

                    <div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '10px'
                      }}>
                        <h4 style={{ margin: 0 }}>{t("AddRecords.partitions.sqlScript")}</h4>
                        <Button onClick={copyToClipboard} className="main-button-peds">
                          {t("AddRecords.partitions.copyToClipboard")}
                        </Button>
                      </div>
                      <Box sx={{ 
                        backgroundColor: '#f5f5f5',
                        padding: '15px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        maxHeight: '400px',
                        overflow: 'auto'
                      }}>
                        <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                          {sqlResult.sqlScript}
                        </pre>
                      </Box>
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <AlertTitle>{t("AddRecords.partitions.warningTitle")}</AlertTitle>
                        {t("AddRecords.partitions.warningMessage")}
                      </Alert>
                    </div>
                  </div>
                )}

                {sqlResult && !sqlResult.success && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <AlertTitle>{t("AddRecords.partitions.errorGeneration")}</AlertTitle>
                    {sqlResult.errorMessage || t("AddRecords.partitions.unknownError")}
                  </Alert>
                )}
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        <div className="upload-instructions">
          <Alert severity="info">
            <AlertTitle>{t("AddRecords.upload.instructionsTitle")}</AlertTitle>
            {t("AddRecords.upload.instructionsDesc")}
            <ul>
              <li><strong>{t("AddRecords.upload.vehiclesFile")}</strong> - {t("AddRecords.upload.vehiclesFileDesc")}</li>
              <li><strong>{t("AddRecords.upload.accidentsFile")}</strong> - {t("AddRecords.upload.accidentsFileDesc")}</li>
              <li><strong>{t("AddRecords.upload.gpsFile")}</strong> - {t("AddRecords.upload.gpsFileDesc")}</li>
              <li><strong>{t("AddRecords.upload.pedestriansFile")}</strong> - {t("AddRecords.upload.pedestriansFileDesc")}</li>
            </ul>
          </Alert>
        </div>

        <div className="folder-upload-section">
            <div style={{
              padding: '30px',
              border: '2px dashed #1976d2',
              borderRadius: '8px',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              marginBottom: '20px'
            }}>
              <input
                type="file"
                // @ts-ignore - webkitdirectory je podporovaný vo väčšine moderných prehliadačoch
                webkitdirectory="true"
                directory="true"
                multiple
                onChange={handleFolderSelect}
                style={{ display: 'none' }}
                id="folder-input"
                disabled={uploading}
              />
              <label htmlFor="folder-input" style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
                <div style={{ fontSize: '48px', marginBottom: '10px' }}>📁</div>
                <h3 style={{ marginBottom: '10px' }}>{t("AddRecords.upload.selectFolder")}</h3>
                {folderPath && (
                  <p style={{ color: '#1976d2', fontWeight: 'bold', marginTop: '10px' }}>
                    {t("AddRecords.upload.selectedFolder")} {folderPath}
                  </p>
                )}
              </label>
            </div>

            {/* Zobrazenie stavu nájdených súborov */}
            {(vehiclesFile.status !== 'empty' || accidentsFile.status !== 'empty' || 
              gpsFile.status !== 'empty' || pedestriansFile.status !== 'empty') && (
              <div className="file-upload-grid">
                <div className={`file-upload-box ${vehiclesFile.status}`}>
                  <div className="file-upload-header">
                    <h3>{t("AddRecords.upload.vehiclesFile")} <span className="required">{t("AddRecords.upload.required")}</span></h3>
                    {getFileIcon(vehiclesFile.status)}
                  </div>
                  <div className="file-upload-body">
                    {vehiclesFile.file ? (
                      <div className="file-info">
                        <span>✓ {vehiclesFile.file.name}</span>
                        <span>{t("AddRecords.upload.fileSize")} {(vehiclesFile.file.size / 1024).toFixed(2)} KB</span>
                      </div>
                    ) : (
                      <div className="file-error">
                        <FiAlertCircle /> {vehiclesFile.error || t("AddRecords.upload.notFound")}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`file-upload-box ${accidentsFile.status}`}>
                  <div className="file-upload-header">
                    <h3>{t("AddRecords.upload.accidentsFile")} <span className="required">{t("AddRecords.upload.required")}</span></h3>
                    {getFileIcon(accidentsFile.status)}
                  </div>
                  <div className="file-upload-body">
                    {accidentsFile.file ? (
                      <div className="file-info">
                        <span>✓ {accidentsFile.file.name}</span>
                        <span>{t("AddRecords.upload.fileSize")} {(accidentsFile.file.size / 1024).toFixed(2)} KB</span>
                      </div>
                    ) : (
                      <div className="file-error">
                        <FiAlertCircle /> {accidentsFile.error || t("AddRecords.upload.notFound")}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`file-upload-box ${gpsFile.status}`}>
                  <div className="file-upload-header">
                    <h3>{t("AddRecords.upload.gpsFile")} <span className="required">{t("AddRecords.upload.required")}</span></h3>
                    {getFileIcon(gpsFile.status)}
                  </div>
                  <div className="file-upload-body">
                    {gpsFile.file ? (
                      <div className="file-info">
                        <span>✓ {gpsFile.file.name}</span>
                        <span>{t("AddRecords.upload.fileSize")} {(gpsFile.file.size / 1024).toFixed(2)} KB</span>
                      </div>
                    ) : (
                      <div className="file-error">
                        <FiAlertCircle /> {gpsFile.error || t("AddRecords.upload.notFound")}
                      </div>
                    )}
                  </div>
                </div>

                <div className={`file-upload-box ${pedestriansFile.status}`}>
                  <div className="file-upload-header">
                    <h3>{t("AddRecords.upload.pedestriansFile")}</h3>
                    {getFileIcon(pedestriansFile.status)}
                  </div>
                  <div className="file-upload-body">
                    {pedestriansFile.file ? (
                      <div className="file-info">
                        <span>✓ {pedestriansFile.file.name}</span>
                        <span>{t("AddRecords.upload.fileSize")} {(pedestriansFile.file.size / 1024).toFixed(2)} KB</span>
                      </div>
                    ) : (
                      <div style={{ color: '#999', fontStyle: 'italic' }}>
                        {t("AddRecords.upload.optionalNotFound")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

        <div className="upload-actions">
          {!uploading ? (
            <Button
              onClick={canUpload ? handleUpload : undefined}
              className={canUpload ? '' : 'disabled'}
            >
              <FiUploadCloud /> {t("AddRecords.upload.uploadButton")}
            </Button>
          ) : (
            <Button
              onClick={handleCancelUpload}
              className="main-button-peds"
              style={{ backgroundColor: '#d32f2f', color: 'white' }}
            >
              <FiX style={{ marginRight: '8px' }} /> {t("AddRecords.upload.cancelButton")}
            </Button>
          )}
          {uploadResult && (
            <Button onClick={resetForm} className="main-button-peds">
              {t("AddRecords.upload.resetButton")}
            </Button>
          )}
        </div>

        {uploading && (
          <div className="upload-progress">
            <Box sx={{ width: '100%', mt: 2 }}>
              <div className="import-status">
                {/* Krok 1: Načítanie súborov */}
                <div className="status-step" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  padding: '12px', 
                  backgroundColor: filesLoaded ? '#e8f5e9' : '#fff3e0',
                  borderLeft: filesLoaded ? '4px solid #4caf50' : '4px solid #ff9800',
                  marginBottom: '10px',
                  borderRadius: '4px'
                }}>
                  {filesLoaded ? (
                    <FiCheckCircle style={{ color: '#4caf50', fontSize: '24px', marginRight: '12px' }} />
                  ) : (
                    <CircularProgress size={24} style={{ marginRight: '12px' }} />
                  )}
                  <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    {t("AddRecords.progress.loadingFiles")} {filesLoaded ? '✓' : '...'}
                  </span>
                </div>

                {/* Krok 2: Importovanie údajov */}
                {filesLoaded && (
                  <div className="status-step" style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    padding: '12px', 
                    backgroundColor: uploadResult ? '#e8f5e9' : '#e3f2fd',
                    borderLeft: uploadResult ? '4px solid #4caf50' : '4px solid #2196f3',
                    marginBottom: '10px',
                    borderRadius: '4px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      {uploadResult ? (
                        <FiCheckCircle style={{ color: '#4caf50', fontSize: '24px', marginRight: '12px' }} />
                      ) : (
                        <CircularProgress size={24} style={{ marginRight: '12px' }} />
                      )}
                      <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        {t("AddRecords.progress.importingData")} {uploadResult ? '✓' : '...'}
                      </span>
                    </div>
                    {/* Real-time progress počas importu */}
                    {(currentImported > 0 || totalToImport > 0) && (
                      <div style={{ marginLeft: '36px', fontSize: '14px' }}>
                        <div style={{ color: '#1976d2', marginBottom: '4px' }}>
                          {t("AddRecords.progress.totalRecords")} <strong>{totalToImport}</strong> {t("AddRecords.progress.recordsLabel")}
                        </div>
                        <div style={{ color: '#2e7d32', marginBottom: '4px' }}>
                          ✓ {t("AddRecords.progress.imported")} <strong>{currentImported}</strong>
                        </div>
                        {currentSkipped > 0 && (
                          <div style={{ color: '#ed6c02', marginBottom: '4px' }}>
                            ⏩ {t("AddRecords.progress.skipped")} <strong>{currentSkipped}</strong>
                          </div>
                        )}
                        <div style={{ color: '#d32f2f' }}>
                          ✗ {t("AddRecords.progress.errors")} <strong>{currentErrors}</strong>
                        </div>
                      </div>
                    )}
                    {/* Finálne výsledky po dokončení */}
                    {uploadResult && (
                      <div style={{ marginLeft: '36px', fontSize: '14px' }}>
                        <div style={{ color: '#2e7d32', marginBottom: '4px' }}>
                          ✓ {t("AddRecords.progress.imported")} <strong>{uploadResult.insertedRecords}</strong>
                        </div>
                        <div style={{ color: '#f57c00', marginBottom: '4px' }}>
                          ⊘ {t("AddRecords.progress.skipped")} <strong>{uploadResult.skippedRecords}</strong>
                        </div>
                        <div style={{ color: '#d32f2f' }}>
                          ✗ {t("AddRecords.progress.errors")} <strong>{uploadResult.errorRecords}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Krok 3: Ukončenie importu */}
                {uploadResult && (
                  <div className="status-step" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '12px', 
                    backgroundColor: importCompleted ? '#e8f5e9' : '#fff3e0',
                    borderLeft: importCompleted ? '4px solid #4caf50' : '4px solid #ff9800',
                    borderRadius: '4px'
                  }}>
                    {importCompleted ? (
                      <FiCheckCircle style={{ color: '#4caf50', fontSize: '24px', marginRight: '12px' }} />
                    ) : (
                      <CircularProgress size={24} style={{ marginRight: '12px' }} />
                    )}
                    <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      {t("AddRecords.progress.completingImport")} {importCompleted ? '✓' : '...'}
                    </span>
                  </div>
                )}
              </div>
            </Box>
          </div>
        )}

        {uploadResult && (
          <div className="upload-results">
            <Alert severity={uploadResult.success ? 'success' : 'error'}>
              <AlertTitle>
                {uploadResult.success ? t("AddRecords.results.completedTitle") : t("AddRecords.results.failedTitle")}
              </AlertTitle>
              
              {/* Dopravné nehody */}
              <div className="results-summary">
                <h4 style={{ marginTop: '10px', marginBottom: '10px' }}>{t("AddRecords.results.trafficAccidents")}</h4>
                <div className="result-item">
                  <strong>{t("AddRecords.results.processedFiles")}</strong> {uploadResult.processedFiles} / {uploadResult.totalFiles}
                </div>
                <div className="result-item">
                  <strong>{t("AddRecords.results.totalRecords")}</strong> {uploadResult.totalRecords}
                </div>
                <div className="result-item success">
                  <FiCheckCircle /> <strong>{t("AddRecords.results.successfullyInserted")}</strong> {uploadResult.insertedRecords}
                </div>
                <div className="result-item warning">
                  <FiAlertCircle /> <strong>{t("AddRecords.results.skippedDuplicates")}</strong> {uploadResult.skippedRecords}
                </div>
                <div className="result-item error">
                  <FiXCircle /> <strong>{t("AddRecords.results.errorsCount")}</strong> {uploadResult.errorRecords}
                </div>
              </div>

              {/* Chodci */}
              {uploadResult.totalPedestrians > 0 && (
                <div className="results-summary" style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                  <h4 style={{ marginBottom: '10px' }}>{t("AddRecords.results.pedestrians")}</h4>
                  <div className="result-item">
                    <strong>{t("AddRecords.results.totalPedestrians")}</strong> {uploadResult.totalPedestrians}
                  </div>
                  <div className="result-item success">
                    <FiCheckCircle /> <strong>{t("AddRecords.results.successfullyInserted")}</strong> {uploadResult.insertedPedestrians}
                  </div>
                  <div className="result-item warning">
                    <FiAlertCircle /> <strong>{t("AddRecords.results.skippedDuplicates")}</strong> {uploadResult.skippedPedestrians}
                  </div>
                  <div className="result-item error">
                    <FiXCircle /> <strong>{t("AddRecords.results.errorsCount")}</strong> {uploadResult.errorPedestrians}
                  </div>
                </div>
              )}

              {Object.keys(uploadResult.fileResults).length > 0 && (
                <div className="file-results">
                  <h4>{t("AddRecords.results.detailedResults")}</h4>
                  {Object.entries(uploadResult.fileResults).map(([fileName, result]) => (
                    <div key={fileName} className="file-result-item">
                      <strong>{fileName}:</strong> {t("AddRecords.results.processed")} {result.processed}
                    </div>
                  ))}
                </div>
              )}

              {uploadResult.errors.length > 0 && (
                <>
                  <Button
                    onClick={() => setShowErrors(!showErrors)}
                    className="main-button-peds"
                    style={{ marginTop: '10px' }}
                  >
                    {showErrors ? t("AddRecords.results.hideErrors") : `${t("AddRecords.results.showErrors")} (${uploadResult.errors.length})`}
                  </Button>
                  <Collapse in={showErrors}>
                    <div className="error-list">
                      {uploadResult.errors.map((error, index) => (
                        <div key={index} className="error-item">
                          <FiXCircle className="error-icon" />
                          <div className="error-details">
                            {error.fileName && <strong>{t("AddRecords.results.file")} {error.fileName}</strong>}
                            {error.rowNumber && <span> ({t("AddRecords.results.row")} {error.rowNumber})</span>}
                            <p>{error.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Collapse>

                  {((uploadResult.failedAccidentInserts?.length ?? 0) > 0 || (uploadResult.failedPedestrianInserts?.length ?? 0) > 0) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', padding: '10px 12px', backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px' }}>
                      <FiFileText style={{ fontSize: '20px', color: '#f57c00', flexShrink: 0 }} />
                      <span style={{ fontSize: '14px', color: '#5d4037' }}>
                        <strong>failed_inserts.xlsx</strong> — {(uploadResult.failedAccidentInserts?.length ?? 0) + (uploadResult.failedPedestrianInserts?.length ?? 0)} {t("AddRecords.results.failedInsertsCount")}
                      </span>
                      <Button
                        onClick={handleDownloadFailedInserts}
                        className="main-button-peds"
                        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        <FiDownload /> {t("AddRecords.results.downloadFailedInserts")}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Alert>
          </div>
        )}
        </>
      )}
      {importMode === 'automated' && (
        <div className="automated-import-section">

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <h3 style={{ margin: 0 }}>{t('AddRecords.automatedImport.lastImport')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', marginTop: '10px', marginRight: '25px' }}>
                <input
                  type="checkbox"
                  checked={scheduleMonthly}
                  onChange={async (e) => {
                    const val = e.target.checked;
                    setScheduleMonthly(val);
                    try {
                      await automatedImportService.saveScheduleMonthly(val);
                    } catch {
                      setScheduleMonthly(!val);
                    }
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                {t('AddRecords.automatedImport.scheduleMonthly')}
              </label>
              <TextField
                label={t('AddRecords.automatedImport.notificationEmail')}
                type="email"
                value={autoEmail}
                onChange={(e) => { setAutoEmail(e.target.value); setAutoEmailSaved(false); }}
                size="small"
                style={{ width: '260px' }}
                placeholder={t('AddRecords.automatedImport.notificationEmailPlaceholder') as string}
              />
              <button type="button" className="button" onClick={handleSaveAutoEmail} disabled={autoEmailSaving || autoEmailSaved} style={{ margin: 0 }}>
                {autoEmailSaving
                  ? <><CircularProgress size={16} style={{ marginRight: '6px' }} />{t('AddRecords.automatedImport.emailSaving')}</>
                  : autoEmailSaved
                    ? t('AddRecords.automatedImport.emailSaved')
                    : t('AddRecords.automatedImport.saveEmail')
                }
              </button>
              <button type="button" className="button" onClick={loadAutomatedData} disabled={automatedLoading} style={{ margin: 0 }}>
                <FiRefreshCw style={{ marginRight: '6px' }} />
                {t('AddRecords.automatedImport.refresh')}
              </button>
            </div>
          </div>

          {automatedLoading && <LinearProgress style={{ marginBottom: '16px' }} />}

          {automatedError && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {automatedError}
            </Alert>
          )}

          {!automatedLoading && !automatedError && (
            <>
              {latestImport === null ? (
                <Alert severity="info" style={{ marginBottom: '16px' }}>
                  {t('AddRecords.automatedImport.noImportYet')}
                </Alert>
              ) : (
                <Alert
                  severity={
                    latestImport.stav === 'SUCCESS' ? 'success'
                    : latestImport.stav === 'PARTIAL_SUCCESS' ? 'warning'
                    : latestImport.stav === 'FAILED' ? 'error'
                    : 'info'
                  }
                  style={{ marginBottom: '16px', position: 'relative' }}
                >
                  {latestImport.stav === 'RUNNING' && (
                    <button
                      type="button"
                      onClick={handleCancelImport}
                      disabled={cancelling}
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '16px',
                        margin: 0,
                        padding: '3px 10px',
                        fontSize: '12px',
                        backgroundColor: '#d32f2f',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: cancelling ? 'not-allowed' : 'pointer',
                        opacity: cancelling ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {cancelling
                        ? <><CircularProgress size={12} style={{ color: '#fff' }} />{t('AddRecords.automatedImport.cancellingImport')}</>
                        : t('AddRecords.automatedImport.cancelImport')
                      }
                    </button>
                  )}
                  <AlertTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span>
                      {t('AddRecords.automatedImport.lastImport')} — {t('AddRecords.automatedImport.year')}: {latestImport.rok}
                      &nbsp;{getStatusChip(latestImport.stav)}
                    </span>
                  </AlertTitle>

                  {latestImport.stav === 'RUNNING' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <CircularProgress size={16} />
                      <span>{t('AddRecords.automatedImport.running')}</span>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '4px 16px', marginTop: '8px' }}>
                    <div><strong>{t('AddRecords.automatedImport.date')}:</strong> {new Date(latestImport.datumImportu).toLocaleString()}</div>
                    {latestImport.casUkoncenia && (
                      <div><strong>{t('AddRecords.automatedImport.finished')}:</strong> {new Date(latestImport.casUkoncenia).toLocaleString()}</div>
                    )}
                    {latestImport.celkovyPocetZaznamov !== null && (
                      <div><strong>{t('AddRecords.automatedImport.totalRecords')}:</strong> {latestImport.celkovyPocetZaznamov}</div>
                    )}
                    {latestImport.vlozeneZaznamy !== null && (
                      <div><strong>{t('AddRecords.automatedImport.inserted')}:</strong> {latestImport.vlozeneZaznamy}</div>
                    )}
                    {latestImport.preskoceneZaznamy !== null && (
                      <div><strong>{t('AddRecords.automatedImport.skipped')}:</strong> {latestImport.preskoceneZaznamy}</div>
                    )}
                    {latestImport.chyboveZaznamy !== null && (
                      <div><strong>{t('AddRecords.automatedImport.errors')}:</strong> {latestImport.chyboveZaznamy}</div>
                    )}
                    {latestImport.celkovyPocetChodcov !== null && (
                      <div><strong>{t('AddRecords.automatedImport.pedestrians')} ({t('AddRecords.automatedImport.totalRecords')}):</strong> {latestImport.celkovyPocetChodcov}</div>
                    )}
                    {latestImport.vlozeniChodci !== null && (
                      <div><strong>{t('AddRecords.automatedImport.pedestrians')} ({t('AddRecords.automatedImport.inserted')}):</strong> {latestImport.vlozeniChodci}</div>
                    )}
                    {latestImport.preskoceniChodci !== null && (
                      <div><strong>{t('AddRecords.automatedImport.pedestrians')} ({t('AddRecords.automatedImport.skipped')}):</strong> {latestImport.preskoceniChodci}</div>
                    )}
                    {latestImport.chyboviChodci !== null && (
                      <div><strong>{t('AddRecords.automatedImport.pedestrians')} ({t('AddRecords.automatedImport.errors')}):</strong> {latestImport.chyboviChodci}</div>
                    )}
                    <div>
                      <strong>{t('AddRecords.automatedImport.partitionCreated')}:</strong>{' '}
                      {latestImport.partitionCreated ? t('AddRecords.automatedImport.yes') : t('AddRecords.automatedImport.no')}
                    </div>
                  </div>

                  {latestImport.errorExcelAvailable && (
                    <div style={{ marginTop: '12px' }}>
                    <button
                        type="button"
                        className="button"
                        onClick={() => handleDownloadErrorExcel(latestImport.id, latestImport.errorExcelFilename)}
                        disabled={downloadingExcel === latestImport.id}
                        style={{ margin: 0 }}
                      >
                        {downloadingExcel === latestImport.id
                          ? <CircularProgress size={16} style={{ marginRight: '6px' }} />
                          : <FiDownload style={{ marginRight: '6px' }} />
                        }
                        {t('AddRecords.automatedImport.downloadErrors')}
                      </button>
                    </div>
                  )}
                </Alert>
              )}

              <Accordion defaultExpanded style={{ marginBottom: '16px', width: 'fit-content', minWidth: 0, backgroundColor: '#f5f5f5', boxShadow: 'none', border: '1px solid #ddd' }}>
                <AccordionSummary expandIcon={null} style={{ paddingLeft: '12px', paddingRight: '12px', minHeight: 0 }}>
                  <strong>{t('AddRecords.automatedImport.triggerImport')}</strong>
                </AccordionSummary>
                <AccordionDetails>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <TextField
                      label={t('AddRecords.automatedImport.year')}
                      type="number"
                      value={triggerYear}
                      onChange={(e) => setTriggerYear(parseInt(e.target.value) || new Date().getFullYear() - 1)}
                      inputProps={{ min: 2000, max: new Date().getFullYear() }}
                      size="small"
                      style={{ width: '120px' }}
                    />
                    <TextField
                      label={t('AddRecords.automatedImport.notificationEmail')}
                      type="email"
                      value={triggerEmail}
                      onChange={(e) => setTriggerEmail(e.target.value)}
                      size="small"
                      style={{ width: '240px' }}
                      placeholder={t('AddRecords.automatedImport.notificationEmailPlaceholder') as string}
                    />
                    <button
                      type="button"
                      className="button"
                      onClick={handleTriggerImport}
                      disabled={triggering}
                      style={{ margin: 0 }}
                    >
                      {triggering
                        ? <><CircularProgress size={16} style={{ marginRight: '6px' }} />{t('AddRecords.automatedImport.triggering')}</>
                        : <><FiDatabase style={{ marginRight: '6px' }} />{t('AddRecords.automatedImport.triggerButton')}</>
                      }
                    </button>
                  </div>
                  {triggerMessage && (
                    <Alert severity={triggerMessageType} style={{ marginTop: '12px' }}>
                      {triggerMessageIsKey ? t(triggerMessage) : triggerMessage}
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>

              {importHistory.length > 0 && (
                <>
                  <h3 style={{ marginBottom: '8px' }}>{t('AddRecords.automatedImport.historyTitle')}</h3>
                  <TableContainer component={Paper} style={{ marginBottom: '16px' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>{t('AddRecords.automatedImport.year')}</strong></TableCell>
                          <TableCell><strong>{t('AddRecords.automatedImport.date')}</strong></TableCell>
                          <TableCell><strong>{t('AddRecords.automatedImport.status')}</strong></TableCell>
                          <TableCell><strong>{t('AddRecords.automatedImport.inserted')}</strong></TableCell>
                          <TableCell><strong>{t('AddRecords.automatedImport.skipped')}</strong></TableCell>
                          <TableCell><strong>{t('AddRecords.automatedImport.errors')}</strong></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {importHistory.map((record) => {
                          const isFailed = record.stav === 'FAILED';
                          return (
                          <TableRow key={record.id} hover>
                            <TableCell>{record.rok}</TableCell>
                            <TableCell>{new Date(record.datumImportu).toLocaleString()}</TableCell>
                            <TableCell>{getStatusChip(record.stav)}</TableCell>
                            <TableCell>
                              {isFailed || (record.vlozeneZaznamy == null && record.vlozeniChodci == null) ? '—'
                                : (record.vlozeneZaznamy ?? 0) + (record.vlozeniChodci ?? 0)}
                            </TableCell>
                            <TableCell>
                              {isFailed || (record.preskoceneZaznamy == null && record.preskoceniChodci == null) ? '—'
                                : (record.preskoceneZaznamy ?? 0) + (record.preskoceniChodci ?? 0)}
                            </TableCell>
                            <TableCell>
                              {isFailed || (record.chyboveZaznamy == null && record.chyboviChodci == null) ? '—'
                                : (record.chyboveZaznamy ?? 0) + (record.chyboviChodci ?? 0)}
                            </TableCell>
                            <TableCell>
                              {record.errorExcelAvailable && (
                                <button
                                  type="button"
                                  className="button"
                                  onClick={() => handleDownloadErrorExcel(record.id, record.errorExcelFilename)}
                                  disabled={downloadingExcel === record.id}
                                  style={{ margin: 0, padding: '4px 8px' }}
                                  title={t('AddRecords.automatedImport.downloadErrors') as string}
                                >
                                  {downloadingExcel === record.id
                                    ? <CircularProgress size={14} />
                                    : <FiDownload />
                                  }
                                </button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </div>
      )}
      </div>
  );
}